// src/components/usuario/BuzonSugerenciasUsuario.jsx
import React, { useEffect, useState } from "react";
import api from "../../../../api";
import { ENDPOINTS } from "../../Utils/Endpoints";
import { getSessionUserId, getSessionEmpresaId } from "../../../../utils/SessionUser";
import AlertDialog from "../../../Lms/Components/Shared/AlertDialog";
import { FiClock, FiCheckCircle, FiTrendingUp } from "react-icons/fi";

const BuzonSugerenciasUsuario = ({ onVolver }) => {
    const [sugerencias, setSugerencias] = useState([]);
    const [categoriasDB, setCategoriasDB] = useState([]);
    const [estadosDB, setEstadosDB] = useState([]);

    // Estado de carga
    const [loading, setLoading] = useState(false);

    // Formulario nueva sugerencia
    const [nuevaSug, setNuevaSug] = useState({
        asunto: "",
        descripcion: "",
        idCategoria: "",
        anonimo: false,
    });
    const [erroresNueva, setErroresNueva] = useState({});

    // Estado para el modal
    const [showModal, setShowModal] = useState(false);
    const [modoModal, setModoModal] = useState('crear'); // 'crear' o 'ver'
    const [sugerenciaSeleccionada, setSugerenciaSeleccionada] = useState(null);

    // Estado para AlertDialog
    const [alertConfig, setAlertConfig] = useState({
        open: false,
        title: "",
        message: "",
        type: "info", // "success", "error", "warning", "info"
        onConfirm: null,
    });

    const showAlert = (title, message, type = "info", onConfirm = null) => {
        setAlertConfig({ open: true, title, message, type, onConfirm });
    };

    const closeAlert = () => {
        setAlertConfig((prev) => ({ ...prev, open: false }));
        if (alertConfig.onConfirm) alertConfig.onConfirm();
    };

    // Cargar datos iniciales
    useEffect(() => {
        const cargarDatos = async () => {
            try {
                setLoading(true);
                const userId = getSessionUserId();
                // Usamos el endpoint filtrado para que el usuario solo vea SUS sugerencias
                const [resSug, resCat, resEst] = await Promise.all([
                    api.get(ENDPOINTS.sugerenciasPorUsuario(userId)),
                    api.get(ENDPOINTS.categorias),
                    api.get(ENDPOINTS.estadosIntranet)
                ]);

                // El API devuelve PascalCase (PropertyNamingPolicy = null en Program.cs)
                // Normalizamos a camelCase aquí para que todo el componente sea consistente
                const normalizarSugerencia = (s) => ({
                    idSugerencia: s.IdSugerencia ?? s.idSugerencia,
                    asunto: s.Asunto ?? s.asunto ?? "",
                    descripcion: s.Descripcion ?? s.descripcion ?? "",
                    idCategoria: s.IdCategoria ?? s.idCategoria,
                    esAnonima: s.EsAnonima ?? s.esAnonima ?? false,
                    idUsuarioAutor: s.IdUsuarioAutor ?? s.idUsuarioAutor,
                    idUsuarioCreador: s.IdUsuarioCreador ?? s.idUsuarioCreador,
                    idResponsable: s.IdResponsable ?? s.idResponsable,
                    idEstado: s.IdEstado ?? s.idEstado,
                    responsable: s.Responsable ?? s.responsable ?? "",
                    comentariosInternos: s.ComentariosInternos ?? s.comentariosInternos ?? "",
                    fechaEnvio: s.FechaEnvio ?? s.fechaEnvio,
                    activo: s.Activo ?? s.activo ?? true,
                });

                const normalizarCatalogo = (c) => ({
                    idCategoria: c.IdCategoria ?? c.idCategoria,
                    nombre: c.Nombre ?? c.nombre ?? "",
                });

                const normalizarEstado = (e) => ({
                    idEstado: e.IdEstado ?? e.idEstado,
                    nombre: e.Nombre ?? e.nombre ?? "",
                    clave: e.Clave ?? e.clave ?? "",
                });

                const sugerenciasNorm = resSug.data.map(normalizarSugerencia);
                const categoriasNorm = resCat.data.map(normalizarCatalogo);
                const estadosNorm = resEst.data.map(normalizarEstado);

                // Filtrar categorías relevantes para buzón de sugerencias
                const categoriasRelevantes = [
                    "recursos humanos", "rh", "capacitacion", "capacitación", "clima laboral",
                    "comunicación", "comunicacion", "servicios internos", "innovación", "innovacion",
                    "calidad", "mejora continua", "procesos", "tecnología", "tecnologia",
                    "cultura organizacional", "seguridad", "social"
                ];

                const categoriasFiltradas = categoriasNorm.filter(c =>
                    categoriasRelevantes.some(r => c.nombre.toLowerCase().includes(r))
                );

                setSugerencias(sugerenciasNorm);
                setCategoriasDB(categoriasFiltradas.length > 0 ? categoriasFiltradas : categoriasNorm);
                setEstadosDB(estadosNorm);

                if (categoriasFiltradas.length > 0) {
                    setNuevaSug(prev => ({ ...prev, idCategoria: categoriasFiltradas[0].idCategoria }));
                } else if (categoriasNorm.length > 0) {
                    setNuevaSug(prev => ({ ...prev, idCategoria: categoriasNorm[0].idCategoria }));
                }

            } catch (error) {
                console.error("Error cargando datos de sugerencias:", error);
            } finally {
                setLoading(false);
            }
        };
        cargarDatos();
    }, []);

    // Helpers — con datos ya normalizados a camelCase
    const getNombreCategoria = (id) => {
        const cat = categoriasDB.find(c => c.idCategoria === id);
        return cat ? cat.nombre : "General";
    };

    const getDatosEstado = (id) => {
        const est = estadosDB.find(e => e.idEstado === id);

        // Default (Recibida)
        let info = { label: "Recibida", clase: "recibida", color: '#0369a1', bg: '#eff6ff', cardBg: '#f0f9ff', icon: <FiClock /> };

        if (!est) return info;

        const buscar = (est.clave || est.nombre || "").toLowerCase();

        if (buscar.includes("recibida")) {
            info = { label: "Recibida", clase: "recibida", color: '#0369a1', bg: '#eff6ff', cardBg: '#f0f9ff', icon: <FiClock /> };
        } else if (buscar.includes("revision") || buscar.includes("revisión")) {
            info = { label: "En revisión", clase: "en_revision", color: '#d97706', bg: '#fffbeb', cardBg: '#fffbeb', icon: <FiTrendingUp /> };
        } else if (buscar.includes("atendida") || buscar.includes("cerrada")) {
            info = { label: "Atendida", clase: "atendida", color: '#15803d', bg: '#f0fdf4', cardBg: '#f0fdf4', icon: <FiCheckCircle /> };
        }

        return info;
    };

    // Handlers
    const handleNuevaSugChange = (e) => {
        const { name, value, type, checked } = e.target;
        setNuevaSug((prev) => ({
            ...prev,
            [name]: type === "checkbox" ? checked : value,
        }));
    };

    const validarNuevaSug = () => {
        const errs = {};
        if (!nuevaSug.asunto.trim()) errs.asunto = "El asunto es obligatorio.";
        if (!nuevaSug.descripcion.trim()) errs.descripcion = "La descripción es obligatoria.";
        if (!nuevaSug.idCategoria) errs.categoria = "Selecciona una categoría.";
        setErroresNueva(errs);
        return Object.keys(errs).length === 0;
    };

    const handleEnviarSugerencia = async (e) => {
        e.preventDefault();
        if (!validarNuevaSug()) return;

        try {
            const estadoRecibida = estadosDB.find(e => (e.clave || e.Clave) === 'recibida') || estadosDB[0];
            const idEstado = estadoRecibida ? (estadoRecibida.idEstado || estadoRecibida.IdEstado) : null;
            const userId = getSessionUserId();

            // Number() es más confiable que parseInt() para valores de <select>
            const idCatNum = Number(nuevaSug.idCategoria);

            const payload = {
                idSugerencia: 0,
                asunto: nuevaSug.asunto,
                descripcion: nuevaSug.descripcion,
                idCategoria: idCatNum > 0 ? idCatNum : null,
                esAnonima: nuevaSug.anonimo,
                idUsuarioAutor: nuevaSug.anonimo ? null : userId,
                idUsuarioCreador: userId,
                idResponsable: null,
                idEstado: idEstado || 1,
                responsable: "Sin asignar",
                comentariosInternos: "Sin comentarios",
                fechaEnvio: new Date().toISOString(),
                fechaModificacion: null,
                idUsuarioModificador: null,
                activo: true,
                idEmpresa: getSessionEmpresaId()
            };

            await api.post(ENDPOINTS.sugerencias, payload);

            // Recargar y normalizar (la API devuelve PascalCase)
            const res = await api.get(ENDPOINTS.sugerenciasPorUsuario(userId));
            setSugerencias(res.data.map(s => ({
                idSugerencia: s.IdSugerencia ?? s.idSugerencia,
                asunto: s.Asunto ?? s.asunto ?? "",
                descripcion: s.Descripcion ?? s.descripcion ?? "",
                idCategoria: s.IdCategoria ?? s.idCategoria,
                esAnonima: s.EsAnonima ?? s.esAnonima ?? false,
                idUsuarioAutor: s.IdUsuarioAutor ?? s.idUsuarioAutor,
                idUsuarioCreador: s.IdUsuarioCreador ?? s.idUsuarioCreador,
                idResponsable: s.IdResponsable ?? s.idResponsable,
                idEstado: s.IdEstado ?? s.idEstado,
                responsable: s.Responsable ?? s.responsable ?? "",
                comentariosInternos: s.ComentariosInternos ?? s.comentariosInternos ?? "",
                fechaEnvio: s.FechaEnvio ?? s.fechaEnvio,
                activo: s.Activo ?? s.activo ?? true,
            })));

            setNuevaSug({
                asunto: "",
                descripcion: "",
                idCategoria: categoriasDB.length > 0 ? (categoriasDB[0].idCategoria || categoriasDB[0].IdCategoria) : "",
                anonimo: false,
            });
            setErroresNueva({});
            setShowModal(false);
            showAlert("Éxito", "Sugerencia enviada correctamente", "success");

        } catch (error) {
            console.error("Error enviando sugerencia:", error);
            const msg = error.response?.data ? JSON.stringify(error.response.data) : error.message;
            showAlert("Error", `Error al enviar la sugerencia: ${msg}`, "error");
        }
    };

    const handleNuevaSugerencia = () => {
        setModoModal('crear');
        setNuevaSug({
            asunto: "",
            descripcion: "",
            idCategoria: categoriasDB.length > 0 ? categoriasDB[0].idCategoria : "",
            anonimo: false,
        });
        setErroresNueva({});
        setShowModal(true);
    };

    const handleVerSugerencia = (sugerencia) => {
        setModoModal('ver');
        setSugerenciaSeleccionada(sugerencia);
        // Los datos ya están normalizados a camelCase desde el useEffect
        setNuevaSug({
            asunto: sugerencia.asunto || "",
            descripcion: sugerencia.descripcion || "",
            idCategoria: sugerencia.idCategoria,
            anonimo: sugerencia.esAnonima || false,
        });
        setErroresNueva({});
        setShowModal(true);
    };

    return (
        <div className="intra-suggestions-page">
            {/* HEADER */}
            <header className="intra-news-header">
                <div>
                    {onVolver && (
                        <button
                            type="button"
                            className="intra-button-back"
                            onClick={onVolver}
                            style={{
                                display: 'inline-flex',
                                alignItems: 'center',
                                gap: '0.5rem',
                                padding: '0.5rem 1rem',
                                marginBottom: '1rem',
                                backgroundColor: 'transparent',
                                border: '1px solid #cbd5e1',
                                borderRadius: '8px',
                                color: '#475569',
                                fontSize: '0.875rem',
                                fontWeight: 500,
                                cursor: 'pointer',
                                transition: 'all 0.2s ease',
                            }}
                            onMouseEnter={(e) => {
                                e.currentTarget.style.backgroundColor = '#f1f5f9';
                                e.currentTarget.style.transform = 'translateX(-4px)';
                            }}
                            onMouseLeave={(e) => {
                                e.currentTarget.style.backgroundColor = 'transparent';
                                e.currentTarget.style.transform = 'translateX(0)';
                            }}
                        >
                            <span style={{ fontSize: '1.1rem', fontWeight: 'bold' }}>←</span>
                            <span>Volver al dashboard</span>
                        </button>
                    )}
                    <h1 className="intra-news-title">Buzón de sugerencias</h1>
                </div>
            </header>

            {/* HEADER CON BOTÓN */}
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '2rem' }}>
                <h2 style={{ fontSize: '1.25rem', fontWeight: 600, color: '#1e293b', margin: 0 }}>Mis sugerencias</h2>
                <button
                    type="button"
                    className="intra-button-primary"
                    onClick={handleNuevaSugerencia}
                    style={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: '0.5rem',
                        padding: '0.625rem 1.25rem',
                        fontSize: '0.875rem',
                        fontWeight: 600,
                    }}
                >
                    <span style={{ fontSize: '1.2rem', lineHeight: 1 }}>+</span>
                    <span>Nueva sugerencia</span>
                </button>
            </div>

            {/* CONTENIDO - GRID DE SUGERENCIAS */}
            {loading && sugerencias.length === 0 ? (
                <p style={{ textAlign: 'center', color: '#94a3b8', padding: '3rem' }}>
                    Cargando sugerencias...
                </p>
            ) : sugerencias.length === 0 ? (
                <div style={{
                    textAlign: 'center',
                    padding: '4rem 2rem',
                    backgroundColor: 'white',
                    borderRadius: '12px',
                    border: '1px solid #e2e8f0',
                }}>
                    <p style={{ fontSize: '1.125rem', color: '#64748b', marginBottom: '0.5rem' }}>
                        Aún no has registrado sugerencias
                    </p>
                    <p style={{ fontSize: '0.875rem', color: '#94a3b8' }}>
                        Haz clic en "Nueva sugerencia" para comenzar
                    </p>
                </div>
            ) : (
                <div style={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))',
                    gap: '1.5rem',
                }}>
                    {sugerencias.map((s) => {
                        const estadoInfo = getDatosEstado(s.idEstado);
                        return (
                            <article
                                key={s.idSugerencia}
                                onClick={() => handleVerSugerencia(s)}
                                style={{
                                    padding: '1.5rem',
                                    backgroundColor: estadoInfo.cardBg,
                                    border: `1.5px solid ${estadoInfo.color}`,
                                    borderRadius: '16px',
                                    transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                                    cursor: 'pointer',
                                    position: 'relative',
                                    overflow: 'hidden',
                                    boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)'
                                }}
                                onMouseEnter={(e) => {
                                    e.currentTarget.style.borderColor = estadoInfo.color;
                                    e.currentTarget.style.boxShadow = `0 10px 15px -3px ${estadoInfo.color}30, 0 4px 6px -2px rgba(0, 0, 0, 0.05)`;
                                    e.currentTarget.style.transform = 'translateY(-4px) scale(1.02)';
                                }}
                                onMouseLeave={(e) => {
                                    e.currentTarget.style.borderColor = `${estadoInfo.color}`;
                                    e.currentTarget.style.boxShadow = '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)';
                                    e.currentTarget.style.transform = 'translateY(0) scale(1)';
                                }}
                            >
                                {/* Pestañita de estado */}
                                <div style={{
                                    position: 'absolute',
                                    top: 0,
                                    right: 0,
                                    width: '45px',
                                    height: '45px',
                                    zIndex: 2,
                                    overflow: 'hidden'
                                }}>
                                    <div style={{
                                        position: 'absolute',
                                        top: 0,
                                        right: 0,
                                        width: 0,
                                        height: 0,
                                        borderStyle: 'solid',
                                        borderWidth: '0 45px 45px 0',
                                        borderColor: `transparent ${estadoInfo.color} transparent transparent`,
                                    }} />
                                    <div style={{
                                        position: 'absolute',
                                        top: '5px',
                                        right: '5px',
                                        color: 'white',
                                        fontSize: '1rem',
                                        zIndex: 3,
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                    }} title={`Estatus: ${estadoInfo.label}`}>
                                        {estadoInfo.icon}
                                    </div>
                                </div>

                                <div style={{ marginBottom: '0.6rem' }}>
                                    <div style={{ marginBottom: '0.6rem' }}>
                                        <span className="intra-chip" style={{
                                            fontSize: '0.65rem',
                                            padding: '4px 10px',
                                            backgroundColor: `${estadoInfo.color}25`,
                                            color: estadoInfo.color,
                                            textTransform: 'uppercase',
                                            letterSpacing: '0.05em',
                                            fontWeight: 800,
                                            border: `1px solid ${estadoInfo.color}50`,
                                            borderRadius: '20px'
                                        }}>
                                            {getNombreCategoria(s.idCategoria || s.IdCategoria)}
                                        </span>
                                    </div>
                                    <h3 style={{ fontSize: '1.125rem', fontWeight: 800, color: '#1e293b', margin: 0, flex: 1, paddingRight: '2.5rem', lineHeight: 1.3 }}>
                                        {s.asunto}
                                    </h3>
                                </div>
                                <p style={{ fontSize: '0.75rem', color: '#64748b', marginBottom: '0.75rem', fontWeight: 600 }}>
                                    {s.fechaEnvio ? new Date(s.fechaEnvio).toLocaleDateString('es-MX', { year: 'numeric', month: 'long', day: 'numeric' }) : 'N/A'}
                                </p>
                                <p style={{ fontSize: '0.875rem', color: '#475569', marginBottom: '1rem', lineHeight: 1.5 }}>
                                    {s.descripcion && s.descripcion.length > 120
                                        ? s.descripcion.slice(0, 120) + "..."
                                        : s.descripcion}
                                </p>
                                <div style={{
                                    paddingTop: '0.75rem',
                                    borderTop: '1px solid #f1f5f9',
                                    display: 'flex',
                                    justifyContent: 'space-between',
                                    alignItems: 'center'
                                }}>
                                    <p style={{ fontSize: '0.75rem', color: '#64748b', margin: 0 }}>
                                        <strong>Responsable:</strong> {s.responsable || "Sin asignar"}
                                    </p>
                                </div>
                            </article>
                        );
                    })}
                </div>
            )}

            {/* MODAL PARA NUEVA/VER SUGERENCIA */}
            {showModal && (
                <div
                    style={{
                        position: 'fixed',
                        top: 0,
                        left: 0,
                        right: 0,
                        bottom: 0,
                        backgroundColor: 'rgba(0, 0, 0, 0.5)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        zIndex: 1000
                    }}
                    onClick={() => setShowModal(false)}
                >
                    <div
                        style={{
                            backgroundColor: 'white',
                            borderRadius: '12px',
                            width: '90%',
                            maxWidth: '700px',
                            maxHeight: '90vh',
                            display: 'flex',
                            flexDirection: 'column',
                            boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)'
                        }}
                        onClick={(e) => e.stopPropagation()}
                    >
                        {/* Modal Header */}
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '1.5rem 2rem', borderBottom: '1px solid #e2e8f0' }}>
                            <h2 style={{ margin: 0, fontSize: '1.25rem', fontWeight: 600, color: '#1e293b' }}>
                                {modoModal === 'crear' ? 'Nueva sugerencia' : 'Detalle de sugerencia'}
                            </h2>
                            <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
                                {modoModal === 'crear' && (
                                    <button
                                        type="button"
                                        onClick={handleEnviarSugerencia}
                                        style={{
                                            background: 'none',
                                            border: 'none',
                                            fontSize: '2rem',
                                            lineHeight: 1,
                                            color: '#64748b',
                                            cursor: 'pointer'
                                        }}
                                        title="Guardar"
                                    >
                                        ✓
                                    </button>
                                )}
                                <button
                                    type="button"
                                    onClick={() => setShowModal(false)}
                                    style={{
                                        background: 'none',
                                        border: 'none',
                                        fontSize: '2rem',
                                        lineHeight: 1,
                                        color: '#64748b',
                                        cursor: 'pointer'
                                    }}
                                >
                                    ×
                                </button>
                            </div>
                        </div>

                        {/* Modal Content - Scrollable */}
                        <div style={{ padding: '2rem', overflowY: 'auto' }}>
                            {modoModal === 'crear' ? (
                                <form onSubmit={handleEnviarSugerencia}>
                                    <div className="intra-form-group">
                                        <label className="intra-label">Asunto</label>
                                        <input
                                            name="asunto"
                                            type="text"
                                            className={`intra-input ${erroresNueva.asunto ? "intra-input-error" : ""}`}
                                            value={nuevaSug.asunto}
                                            onChange={handleNuevaSugChange}
                                            placeholder="Ej. Propuesta para mejorar el proceso de inducción"
                                        />
                                        {erroresNueva.asunto && <p className="intra-error-text">{erroresNueva.asunto}</p>}
                                    </div>

                                    <div className="intra-form-group">
                                        <label className="intra-label">Descripción</label>
                                        <textarea
                                            name="descripcion"
                                            className={`intra-textarea ${erroresNueva.descripcion ? "intra-input-error" : ""}`}
                                            rows={6}
                                            value={nuevaSug.descripcion}
                                            onChange={handleNuevaSugChange}
                                            placeholder="Describe tu sugerencia, el problema que resuelve y el beneficio que genera."
                                        />
                                        {erroresNueva.descripcion && <p className="intra-error-text">{erroresNueva.descripcion}</p>}
                                    </div>

                                    <div className="intra-form-row">
                                        <div className="intra-form-group">
                                            <label className="intra-label">Categoría</label>
                                            <select
                                                name="idCategoria"
                                                className="intra-select"
                                                value={nuevaSug.idCategoria}
                                                onChange={handleNuevaSugChange}
                                            >
                                                {categoriasDB.map((c) => (
                                                    <option key={c.idCategoria} value={c.idCategoria}>
                                                        {c.nombre}
                                                    </option>
                                                ))}
                                            </select>
                                        </div>

                                        <div className="intra-form-group">
                                            <label className="intra-label">Identificación</label>
                                            <label style={{ display: 'flex', alignItems: 'center', gap: '0.6rem', marginTop: '0.5rem', cursor: 'pointer' }}>
                                                <div className="intra-switch">
                                                    <input
                                                        type="checkbox"
                                                        name="anonimo"
                                                        checked={nuevaSug.anonimo}
                                                        onChange={handleNuevaSugChange}
                                                    />
                                                    <span className="intra-switch-slider"></span>
                                                </div>
                                                <span style={{ fontSize: '0.875rem', color: '#334155', fontWeight: 500 }}>Enviar como anónimo</span>
                                            </label>
                                        </div>
                                    </div>
                                </form>
                            ) : (
                                <div style={{ display: 'flex', flexDirection: 'column', gap: '1.25rem' }}>
                                    <div>
                                        <label style={{ fontSize: '0.7rem', fontWeight: 800, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.05em', display: 'block', marginBottom: '0.25rem' }}>Asunto</label>
                                        <p style={{ fontSize: '1.125rem', fontWeight: 700, color: '#1e293b', margin: 0 }}>{sugerenciaSeleccionada?.asunto}</p>
                                    </div>

                                    <div>
                                        <label style={{ fontSize: '0.7rem', fontWeight: 800, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.05em', display: 'block', marginBottom: '0.25rem' }}>Descripción</label>
                                        <p style={{ 
                                            fontSize: '0.9375rem', 
                                            color: '#475569', 
                                            lineHeight: 1.6, 
                                            margin: 0, 
                                            whiteSpace: 'pre-wrap' 
                                        }}>
                                            {sugerenciaSeleccionada?.descripcion}
                                        </p>
                                    </div>

                                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1.5rem', paddingTop: '0.5rem' }}>
                                        <div>
                                            <label style={{ fontSize: '0.7rem', fontWeight: 800, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.05em', display: 'block', marginBottom: '0.25rem' }}>Categoría</label>
                                            <p style={{ margin: 0, fontWeight: 600, color: '#334155' }}>{getNombreCategoria(sugerenciaSeleccionada?.idCategoria)}</p>
                                        </div>
                                        <div>
                                            <label style={{ fontSize: '0.7rem', fontWeight: 800, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.05em', display: 'block', marginBottom: '0.25rem' }}>Identificación</label>
                                            <p style={{ margin: 0, fontWeight: 600, color: '#334155' }}>{sugerenciaSeleccionada?.esAnonima ? "Anónimo" : "Identificado"}</p>
                                        </div>
                                    </div>

                                    {sugerenciaSeleccionada?.comentariosInternos && sugerenciaSeleccionada?.comentariosInternos !== "Sin comentarios" && (
                                        <div style={{ 
                                            marginTop: '0.5rem',
                                            padding: '1.25rem', 
                                            backgroundColor: '#f0fdf4', 
                                            borderRadius: '12px', 
                                            border: '1px solid #10b98140' 
                                        }}>
                                            <label style={{ fontSize: '0.7rem', fontWeight: 900, color: '#10b981', textTransform: 'uppercase', letterSpacing: '0.05em', display: 'block', marginBottom: '0.5rem' }}>
                                                Comentario de seguimiento
                                            </label>
                                            <p style={{ fontSize: '0.9375rem', color: '#15803d', lineHeight: 1.6, margin: 0, whiteSpace: 'pre-wrap' }}>
                                                {sugerenciaSeleccionada.comentariosInternos}
                                            </p>
                                        </div>
                                    )}

                                    <div style={{ marginTop: '0.75rem', padding: '1.25rem', backgroundColor: '#f8fafc', borderRadius: '12px', border: '1px solid #e2e8f0' }}>
                                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.75rem' }}>
                                            <p style={{ fontSize: '0.85rem', color: '#64748b', margin: 0 }}>
                                                <strong>Estado:</strong> <span style={{ color: getDatosEstado(sugerenciaSeleccionada?.idEstado).color, fontWeight: 700 }}>{getDatosEstado(sugerenciaSeleccionada?.idEstado).label}</span>
                                            </p>
                                            <p style={{ fontSize: '0.85rem', color: '#64748b', margin: 0 }}>
                                                <strong>Responsable:</strong> <span style={{ color: '#334155', fontWeight: 600 }}>{sugerenciaSeleccionada?.responsable || "Sin asignar"}</span>
                                            </p>
                                            <p style={{ fontSize: '0.85rem', color: '#64748b', margin: 0, gridColumn: 'span 2' }}>
                                                <strong>Fecha de envío:</strong> <span style={{ color: '#334155', fontWeight: 600 }}>{sugerenciaSeleccionada?.fechaEnvio ? new Date(sugerenciaSeleccionada.fechaEnvio).toLocaleDateString('es-MX', { year: 'numeric', month: 'long', day: 'numeric' }) : 'N/A'}</span>
                                            </p>
                                        </div>
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            )}
            {/* Alerta general */}
            <AlertDialog
                open={alertConfig.open}
                onClose={closeAlert}
                title={alertConfig.title}
                message={alertConfig.message}
                variant={alertConfig.type}
            />
        </div>
    );
};

export default BuzonSugerenciasUsuario;
